<script lang="ts" setup>

import {getProductSeats, Price, Product} from "~app/stripe";
import StripePriceCard from "~components/sales/StripePriceCard.vue";
import {computed} from "vue";
import {UserInfo} from "@/common/utils/auth";

const props = defineProps<{
  user: UserInfo
  prices: Record<string, Price>
  email: string
  purchased: string[]
}>()

function planPoints(product: Product): string[] {
  const common = [
    "Get guidance and ask questions during live webinars with master reading teachers",
    "Access and training to our full reading evaluation, allowing you to pinpoint specific skills your child needs to become successful",
    "Hundreds of K-6 comprehension activities, fluency practice videos & thousands of worksheets",
    "Full access to 5 years of pre-made, weekly lessons",
    "Kids can work independently and self-paced, or parent-guided week by week",
    "Video recordings showing student fluency improvement",
    "Track weekly, monthly, and yearly reading progress and improvement",
    "Online support, weekly teacher tips, and access to new features",
  ]

  const seats = getProductSeats(product)

  return [
    ...(seats ? [`Up to ${seats} Individual Student Accounts`] : []),
    ...common
  ]
}

const _tutoringPoints = [
  "One-on-one reading evaluation identifying needs in phonemic awareness, phonics, fluency & comprehension",
  "An approach to reading that's fun, engaging, and highly effective",
  "A detailed 10-15 page reading summary outlining instructional strategies and goals",
  "Annual subscription to the Lyrics2Learn reading software",
  "1-3 grade-level increase in reading skills, confidence, and ENJOYMENT in reading",
  "Monthly progress reports to track your child's improvement",
  "A collaborative relationship with passionate master teachers committed to your child's success",
  "Scheduled one-on-one Zoom meetings every 12 weeks with reading specialists to discuss progress and set goals",
]

function tutoringPoints(plan: Price): string[] {
  const months = plan.lookup_key.includes('3_month') ? 3 : 6;
  return [
    `${months} months of personalized instruction with a master tutor`,
    ..._tutoringPoints,
  ]
}

const homeschool_plan = computed(() => props.prices['plan_homeschool_yearly'])
const tutor_6_month = computed(() => props.prices['plan_tutoring_6_month'])
const tutor_3_month = computed(() => props.prices['plan_tutoring_3_month'])

</script>

<template>
  <main>
    <div class="product-cards">
      <StripePriceCard
        v-if="homeschool_plan"
        class="plan-card"
        :price="homeschool_plan"
        period="once"
        :points="planPoints(homeschool_plan.product)"
        :user="user"
      >
        <template v-if="purchased.includes(homeschool_plan.id)" #cta>
          <div class="already-purchased">You have an active subscription of this type</div>
        </template>
      </StripePriceCard>

      <StripePriceCard
        v-if="tutor_6_month"
        class="tutor-card-6-month"
        :price="tutor_6_month"
        period="once"
        :points="tutoringPoints(tutor_6_month)"
        :user="user"
      >
        <template v-if="purchased.includes(tutor_6_month.id)" #cta>
          <div class="already-purchased">Your purchase is currently active</div>
        </template>
      </StripePriceCard>

      <StripePriceCard
        v-if="tutor_3_month"
        class="tutor-card-3-month"
        :price="tutor_3_month"
        period="once"
        :points="tutoringPoints(tutor_3_month)"
        :user="user"
      >
        <template v-if="purchased.includes(tutor_3_month.id)" #cta>
          <div class="already-purchased">Your purchase is currently active</div>
        </template>
      </StripePriceCard>
    </div>

    <div class="bottom-notice alert-soft-info">
      <p>Have any questions? Please contact Jeremy Spartz directly at
        admin@lyrics2learn.com or (719) 645-8587 and he will contact you as soon as possible.</p>
    </div>

    <!--<pre>{{ props }}</pre>-->
  </main>
</template>

<style scoped lang="scss">
@import "../../../sass/theme";
main {
  margin-top: 2rem;
}

.product-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 1rem;

  * {
    min-width: 15rem;
    max-width: 25rem;
  }
}

.plan-card {
  background-color: $cs-l2l-light-yellow;
}

.tutor-card-6-month {
  background-color: lighten($cs-l2l-light-purple, 5%);
  color: black;
}

.tutor-card-3-month {
  background-color: #eee;
  color: black;
}


.cta-button {
  max-width: 75%;
  margin: 2rem;
}

.title {
  font-size: 1.5rem;
  font-weight: bold;
}

.bottom-notice {
  margin: 1rem auto;
  padding: 0.5rem;
  border-radius: 1rem;
  max-width: 60rem;
}

.already-purchased {
  font-style: italic;
  background-color: lighten($cs-l2l-light-yellow, 5%);
  border: 2px solid $cs-l2l-light-purple;
  border-radius: 0.5rem;
  padding: 0.5rem;
  margin: 0.5rem;
}
</style>
